import { useSelector } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import { getLiveRow } from "../grid/useRowState";
import { getPageRowOffset } from "../tableOptions";
import type { DataGridCellProps } from "./types";
import styles from "./cellContent.module.scss";

/**
 * The row's 1-based position in display order (after filtering, sorting,
 * grouping, and expanding), not its index in the data. Numbers continue
 * across pages: with server pagination the rows only hold the current page,
 * so the page offset is added (page 2 of 50-row pages starts at 51). Server
 * pages given only a page count restart at 1.
 */
export const RowNumberCell = <TData extends RowData>({
    context,
}: Readonly<DataGridCellProps<TData>>) => {
    const { table, row } = context;
    const isServerPaginated = table.options.manualPagination === true;
    // Display order follows sorting, filters, grouping, expansion, and
    // pagination, so this selects from the whole store; `getLiveRow` because a
    // data change keeps unchanged rows' old row objects.
    const rowNumber = useSelector(table.store, () => {
        const displayIndex = getLiveRow(row).getDisplayIndex();
        if (displayIndex < 0) {
            return null;
        }
        const pageOffset = isServerPaginated ? getPageRowOffset(table) : 0;
        return pageOffset + displayIndex + 1;
    });
    return rowNumber === null ? null : (
        <span className={styles.rowNumber}>{rowNumber}</span>
    );
};
